import api from './api';

export interface Defect {
  _id?: string;
  id?: string;
  description: string;
  controlItemGroupName: string;
  parentControlItemGroupName: string;

  // Mevcut alanlar (API'den gelen)
  label?: string;
  solution?: string;

  // AI tarafından üretilen alanlar
  generatedGroup?: string;
  generatedParentGroup?: string;
  generatedTag?: string;
  generatedSolution?: string;
  generatedAt?: string;
  generatedUpdate?: string;

  // Diğer alanlar
  validation?: boolean;
  level?: string;
  status?: string;
  workStationName?: string;
  longVin?: string;
  vehicleModelName?: string;
  vehicleGroupName?: string;
  tags?: string[];
  startedAtDate?: string;
  submitedAtDate?: string;

  // Backward compatibility
  generatedGroupName?: string;
}

export interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  isApproximate?: boolean;
}

export interface ValidationStatsInfo {
  totalValidatedTrue: number;
  totalValidatedFalse: number;
  isApproximate?: boolean;
}

export interface DefectsResponse {
  success: boolean;
  data: Defect[];
  pagination?: PaginationInfo;
  count?: number; // Backward compatibility
  validationStats?: ValidationStatsInfo;
}

export interface DefectQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
  vehicleModelName?: string;
  parentControlItemGroupName?: string;
  controlItemGroupName?: string;
  startedAtDate?: string;
  generatedAtDate?: string;
  level?: string;
  validation?: boolean;
  description?: string;
}

export const defectService = {
  async getAll(params: DefectQueryParams = {}): Promise<DefectsResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.status) queryParams.append('status', params.status);
    if (params.vehicleModelName) queryParams.append('vehicleModelName', params.vehicleModelName);
    if (params.parentControlItemGroupName) queryParams.append('parentControlItemGroupName', params.parentControlItemGroupName);
    if (params.controlItemGroupName) queryParams.append('controlItemGroupName', params.controlItemGroupName);
    if (params.startedAtDate) queryParams.append('startedAtDate', params.startedAtDate);
    if (params.level) queryParams.append('level', params.level);
    if (params.validation !== undefined) queryParams.append('validation', params.validation.toString());
    if (params.description) queryParams.append('description', params.description);

    const url = `/api/defects${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get<DefectsResponse>(url);
    return response.data;
  },

  async create(defect: Defect): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/api/defect', defect);
    return response.data;
  },

  async createMany(defects: Defect[]): Promise<{ success: boolean; message: string; insertedCount: number }> {
    const response = await api.post('/api/defects', defects);
    return response.data;
  },

  async updateValidation(defectId: string, validation: boolean): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/api/data/validate/${defectId}`, {
      validation,
    });
    return response.data;
  },

  async bulkValidateByDescription(description: string, validation: boolean, options?: {
    controlItemGroupName?: string;
    parentControlItemGroupName?: string;
    tags?: string[];
  }): Promise<{ success: boolean; message: string; updatedCount: number; matchedCount: number }> {
    const response = await api.post('/api/data/validate-bulk', {
      description,
      validation,
      ...options,
    });
    return response.data;
  },

  async updateSuggestions(defectId: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
    generatedSolution?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/api/data/update-suggestions/${defectId}`, suggestions);
    return response.data;
  },

  async bulkUpdateSuggestions(description: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }): Promise<{ success: boolean; message: string; updatedCount: number; matchedCount: number }> {
    const response = await api.post('/api/data/update-suggestions-bulk', {
      description,
      ...suggestions,
    });
    return response.data;
  },

  async delete(defectId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/api/defects/${defectId}`);
    return response.data;
  },

  async getHistory(params: DefectQueryParams = {}): Promise<DefectsResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.status) queryParams.append('status', params.status);
    if (params.vehicleModelName) queryParams.append('vehicleModelName', params.vehicleModelName);
    if (params.parentControlItemGroupName) queryParams.append('parentControlItemGroupName', params.parentControlItemGroupName);
    if (params.controlItemGroupName) queryParams.append('controlItemGroupName', params.controlItemGroupName);
    if (params.generatedAtDate) queryParams.append('generatedAtDate', params.generatedAtDate);
    if (params.level) queryParams.append('level', params.level);
    if (params.validation !== undefined) queryParams.append('validation', params.validation.toString());
    if (params.description) queryParams.append('description', params.description);

    const url = `/api/history${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get<DefectsResponse>(url);
    return response.data;
  },
};
